import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export interface PaginationParams {
  page: number;
  setPage: (page: number) => void;
}

/**
 * Hook to manage pagination via URL search params
 *
 * Default: Page 1
 * URL format: ?page=N (omitted if page=1)
 */
export function usePaginationParams(): PaginationParams {
  const [searchParams, setSearchParams] = useSearchParams();

  const page = useMemo((): number => {
    const pageStr = searchParams.get('page');
    if (!pageStr) return 1;

    const parsed = parseInt(pageStr, 10);
    return isNaN(parsed) || parsed < 1 ? 1 : parsed;
  }, [searchParams]);

  const setPage = (newPage: number): void => {
    setSearchParams(
      (prev): URLSearchParams => {
        if (newPage === 1) {
          // Remove page param for page 1 (cleaner URLs)
          prev.delete('page');
        } else {
          prev.set('page', String(newPage));
        }
        return prev;
      },
      { replace: true }
    );
  };

  return {
    page,
    setPage,
  };
}
